import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import Table from 'shell/components/shared/table';
import { PersonVM } from 'shell/view-models/search-person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PmiButton } from 'shell/components/shared/button';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { __ACT_API_URL__, __PERSON_API_URL__ } from 'shell/constants/environment';
import { transformerPersonGeneralityList } from './transformer-anpr';
import { noop } from 'shell/utils/noop';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import {
  AccountholderFormVM,
  CertificationFormVM,
} from 'civil-status/view-models/document-birth-form';
import { headCells } from './constants';
import PmiTextField from 'shell/components/shared/text-field';
import ListGender from 'shell/components/shared/gender';
import ChooseDate from 'shell/components/shared/choice-date';
import { ETA_VIEW } from 'shell/components/shared/choice-date/constants';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { ResponseANPRANSC } from 'shell/models/ansc-person';
import { RadioGroup } from '@mui/material';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import { Radio } from '@mui/material';

const AccordionAccountholderFormComponent = (): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const formContext = useFormikContext<CertificationFormVM>();

  const { accountholder: values = {} } = formContext.values;

  const errors = formContext.errors.accountholder as FormikErrors<AccountholderFormVM>;

  const touched = formContext.touched
    .accountholder as unknown as FormikTouched<AccountholderFormVM>;

  const [visibilityTable, setVisibilityTable] = useState<boolean>(false);

  const onChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('accountholder', {
        ...(formContext.values.accountholder || {}),
        [key]: value,
      });
    },
    [formContext.setFieldValue, formContext.values.accountholder]
  );

  const onChangeValues = useCallback(
    (accountholderValues: AccountholderFormVM) => {
      formContext.setFieldValue('accountholder', {
        ...(formContext.values.accountholder || {}),
        ...(accountholderValues || {}),
      });
    },
    [formContext.setFieldValue, formContext.values.accountholder]
  );

  const onResetValues = useCallback(
    (accountholderValues: AccountholderFormVM) => {
      formContext.setFieldValue('accountholder', {
        ...(accountholderValues || {}),
      });
    },
    [formContext.setFieldValue, formContext.values.accountholder]
  );

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const handleSameApplicant = useEventCallback(() => {
    if (formContext?.values?.flagInternal) {
      return;
    }

    if (!values?.sameAsApplicant) {
      onChange('sameAsApplicant', !values?.sameAsApplicant);
      return;
    }

    onResetValues({ sameAsApplicant: !values?.sameAsApplicant });
  });

  // Handle accountholder
  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseANPRANSC>, AnprVM<CertificationFormVM>>(
    `${__ACT_API_URL__}/certificazione/ricerca-soggetti`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const getPerson = useHttpClient<HttpResponse<ResponseANPRANSC>, AnprVM<CertificationFormVM>>(
    `${__PERSON_API_URL__}/persona/ansc/intestatario`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const showTooltip = useMemo(() => {
    if (values?.fiscalCode || (values?.name && values?.surname) || values?.idNational) {
      if (errors?.searchAccountholder) {
        return true;
      }
      return false;
    }

    if (!values?.fiscalCode || (!values?.name && !values?.surname) || !values?.idNational) {
      return true;
    }

    return false;
  }, [values, errors?.searchAccountholder]);

  const tooltipMessage = useMemo(() => {
    if (values?.fiscalCode || (values?.name && values?.surname) || values?.idNational) {
      if (errors?.searchAccountholder) {
        return errors?.searchAccountholder;
      }
      return;
    }
    if (!values?.fiscalCode || (!values?.name && !values?.surname) || !values?.idNational) {
      return 'Inserire codice fiscale o cognome e nome o ID Ansc';
    }
  }, [values, errors?.searchAccountholder]);

  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);

    if (!values?.fiscalCode && !(values?.name && values?.surname) && !values?.idNational) {
      return;
    }

    const personaRichiesta = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: values?.fiscalCode,
        name: values?.name,
        surname: values?.surname,
        withoutName: values?.withoutName,
        withoutSurname: values?.withoutSurname,
        gender: values?.gender,
        birthday: values?.birthDate,
        birthdayChoice: values?.birthChoiceDate,
        isForeign: values?.isForeign,
        foreignMunicipality: values?.foreignMunicipality,
        foreignStateBirth: values?.foreignState,
        municipalityOfBirth: values?.municipality,
        provinceOfBirth: values?.municipality?.province || undefined,
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
    });

    const criteriRicercaSoggetto = {
      codiceFiscale: values.fiscalCode || undefined,
      cognome: values.surname || undefined,
      nome: values.name || undefined,
      datanascita: values.birthDate ? values.birthDate.toFormat('yyyy-MM-dd') : undefined,
      sesso: values.gender || undefined,
      idComuneNascita: values.municipality?.code || undefined,
      nomeComuneNascita: values.municipality?.description || undefined,
      siglaProvinciaNascita: values.municipality?.province || undefined,
      codiceStatoNascita: values?.foreignState?.id || undefined,
      nomeStatoNascita: values?.foreignState?.value || undefined,
    };

    (
      getAnpr({
        data: {
          personaRichiesta,
          criteriRicercaSoggetto,
          idAnsc: values.idNational || undefined,
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificationFormVM>;

      if (castedResponse?.errors?.length > 0) {
        setVisibility({});
        return;
      }

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];

        onChangeValues({
          searchAccountholder: true,
          ...element.accountholder,
        });
        return;
      }
      resetPagination();
      setVisibilityTable(true);
    });
  });

  const handleClickSelect = useEventCallback((person: CertificationFormVM) => {
    if (person.accountholder?.isANSC) {
      onChangeValues({
        searchAccountholder: true,
        ...person.accountholder,
      });

      resetPagination();
      setVisibilityTable(false);
      return;
    }

    (
      getPerson({
        data: {
          criteriRicerca: {
            codiceFiscale: person.accountholder?.fiscalCode || undefined,
            idSchedaSoggettoANPR: person?.accountholder?.idSubjectAnpr || undefined,
          },
          datiRichiesta: {
            comuneResidenza:
              userProfile?.datiLogin?.profile?.feature?.anpr === '1'
                ? {
                    codiceIstat: userProfile?.datiLogin?.codiceIstat,
                    nomeComune: userProfile?.datiLogin?.nomeComune,
                    siglaProvinciaIstat: userProfile?.datiLogin?.siglaProvincia,
                  }
                : undefined,
            ricercaNazionale: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? '1' : '0',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseANPRANSC>, AnprVM<CertificationFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificationFormVM>;

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        onChangeValues({
          searchAccountholder: true,
          ...element.accountholder,
        });
        setVisibilityTable(false);
        resetPagination();
      }
    });
  });

  const onChangeGender = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('gender', event.target.value);
  });

  const onChangeChooseDate = useEventCallback(
    (date: DateTime | null, choice?: Array<DatePickerView>, age?: string) => {
      const isEta = Boolean(choice?.includes(ETA_VIEW));

      onChangeValues({
        birthDate: isEta ? null : date,
        birthChoiceDate: choice,
        age: isEta ? age : undefined,
      });
    }
  );

  const castedAnprData = anpr.data as AnprVM<CertificationFormVM>;

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        fiscalCode: person.accountholder?.fiscalCode || '',
        surname: person.accountholder?.surname || '',
        name: person.accountholder?.name || '',
        gender: person.accountholder?.gender || '',
        birthday: person.accountholder?.birthDate?.toFormat('dd/MM/yyyy') || '',
        birthplace: person.accountholder?.birthplace,
        residence: person.accountholder?.residence,
        state: person.accountholder?.state,
        anpr: person.accountholder?.idSubjectAnpr,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, handleClickSelect]);

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const onChangeRadio = useEventCallback(() => {
    onChangeValues({
      municipality: null,
      foreignState: null,
      foreignMunicipality: null,
      isForeign: !values?.isForeign,
    });
  });

  const onChangeForeignState = useEventCallback(
    (foreignState: ForeignStateVM | null | undefined) => {
      onChange('foreignState', foreignState);
    }
  );

  const onChangeMunicipality = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipality', municipality);
  });

  const onChangeForeignMunicipality = useEventCallback(
    (foreignMunicipality: MunicipalityAnprVM | null) => {
      onChange('foreignMunicipality', foreignMunicipality);
    }
  );

  return (
    <Fragment>
      <Grid container spacing={0} columnSpacing={1} rowSpacing={1}>
        <Grid item xs={12} md={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="sameApplicant"
                checked={values?.sameAsApplicant || false}
                onChange={handleSameApplicant}
                inputProps={{ 'aria-label': "il richiedente coincide con l'intestatario" }}
                disabled={formContext.values.flagInternal}
              />
            }
            label="Il richiedente coincide con l'intestatario"
          />
        </Grid>
        {!values?.sameAsApplicant ? (
          <Fragment>
            {errorMessage.visibility ? (
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Error}
                  title={errorMessage.title || ''}
                  messages={errorMessage.messages || []}
                  visibility={errorMessage.visibility || false}
                  onChangeVisibility={onVisibility}
                />
                <SearchMessages
                  type={MessageType.Warning}
                  title={`Ci sono ${castedAnprData?.warnings?.length || ''} warning`}
                  messages={castedAnprData?.warnings || []}
                  visibility={
                    visibility.warning !== undefined
                      ? visibility.warning
                      : (castedAnprData?.warnings || [])?.length > 0
                  }
                  onChangeVisibility={onVisibility}
                />
              </Grid>
            ) : undefined}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item container spacing={1} xs={12}>
              {formContext?.values?.flagInternal ? (
                <Grid item xs={12} md={8}>
                  <PmiTextField
                    label="Motivo rilascio *"
                    aria-label="motivo rilascio"
                    id="releaseReason"
                    fullWidth
                    value={values?.reasonRelease || ''}
                    error={touched?.reasonRelease && Boolean(errors?.reasonRelease)}
                    helperText={errors?.reasonRelease}
                    multiline
                    type="text"
                    rows={2}
                    onChange={(event) => {
                      onChange('reasonRelease', event?.target.value);
                    }}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12} md={6}>
                <PmiTextField
                  label="Codice fiscale *"
                  aria-label="Codice fiscale"
                  name="fiscalCode"
                  id="fiscalCode"
                  fullWidth
                  value={values?.fiscalCode?.toUpperCase() || ''}
                  onChange={(event) => onChange('fiscalCode', event.target.value)}
                  inputProps={{
                    maxLength: '16',
                  }}
                  error={touched?.fiscalCode && Boolean(errors?.fiscalCode)}
                  helperText={errors?.fiscalCode}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  label="ID Anpr"
                  aria-label="ID Anpr"
                  name="idSubjectAnpr"
                  id="idSubjectAnpr"
                  fullWidth
                  value={values?.idSubjectAnpr || ''}
                  onChange={noop}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  label="ID Ansc Soggetto"
                  aria-label="ID Ansc Soggetto"
                  name="idAnsc"
                  id="idAnsc"
                  fullWidth
                  value={values?.idAnsc || ''}
                  onChange={noop}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  label="Cognome *"
                  aria-label="Cognome"
                  id="surname"
                  fullWidth
                  value={values?.surname || ''}
                  onChange={(event) => onChange('surname', event.target.value)}
                  error={touched?.surname && Boolean(errors?.surname)}
                  helperText={errors?.surname}
                  disabled={values?.withoutSurname}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="withoutSurname"
                      checked={values.withoutSurname || false}
                      onChange={() => {
                        onChangeValues({
                          withoutSurname: !values.withoutSurname,
                          surname: '',
                        });
                      }}
                      inputProps={{ 'aria-label': 'senza cognome' }}
                    />
                  }
                  label="Persona senza cognome"
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  label="Nome *"
                  aria-label="Nome"
                  id="name"
                  fullWidth
                  value={values.name || ''}
                  onChange={(event) => onChange('name', event.target.value)}
                  error={touched?.name && Boolean(errors?.name)}
                  helperText={errors?.name}
                  disabled={values.withoutName}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="withoutName"
                      checked={values.withoutName || false}
                      onChange={() => {
                        onChangeValues({
                          withoutName: !values.withoutName,
                          name: '',
                        });
                      }}
                      inputProps={{ 'aria-label': 'senza nome' }}
                    />
                  }
                  label="Persona senza nome"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ListGender
                  value={values.gender || ''}
                  onChange={onChangeGender}
                  required
                  error={touched?.gender && Boolean(errors?.gender)}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={12} container spacing={2} alignItems="center">
                  <ChooseDate
                    label="Data di nascita"
                    selected={values.birthDate || null}
                    onChange={onChangeChooseDate}
                    withAge
                    age={values.age || ''}
                    required
                    choice={values.birthChoiceDate}
                    helperText={errors?.birthDate}
                    error={touched?.birthDate && Boolean(errors?.birthDate)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label="Nascita"
                  id="isForeign"
                  defaultValue={false}
                  value={values?.isForeign || false}
                  onChange={() => onChangeRadio()}
                >
                  <FormControlLabel value="false" control={<Radio />} label="Nascita Italia" />
                  <FormControlLabel value="true" control={<Radio />} label="Nascita Estero" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {values?.isForeign ? (
              <Fragment>
                <Grid item xs={12} md={4}>
                  <ForeignStateList
                    value={values?.foreignState || null}
                    onChange={onChangeForeignState}
                    label="Stato estero di nascita"
                    id="foreignStateBirth"
                    required
                    error={touched?.foreignState && Boolean(errors?.foreignState)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MunicipalityForeignList
                    value={values?.foreignMunicipality || null}
                    onChange={onChangeForeignMunicipality}
                    label="Comune estero"
                    id="foreignMunicipality"
                    disabled={!values?.foreignState}
                    foreignStateIstatCode={values?.foreignState?.id}
                    error={touched?.foreignMunicipality && Boolean(errors?.foreignMunicipality)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Provincia o Contea"
                    label="Provincia/Contea"
                    id="foreignProvince"
                    name="foreignProvince"
                    fullWidth
                    type="text"
                    disabled
                    variant="outlined"
                    value={values?.foreignMunicipality?.province || ''}
                  />
                </Grid>
              </Fragment>
            ) : (
              <Fragment>
                <Grid item xs={12} md={4}>
                  <MunicipalityList
                    label="Comune"
                    fromDate={values?.birthDate}
                    required
                    value={values?.municipality || null}
                    onChange={onChangeMunicipality}
                    error={touched?.municipality && Boolean(errors?.municipality)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    label="Provincia"
                    aria-label="Provincia"
                    id="province"
                    name="province"
                    fullWidth
                    onChange={noop}
                    value={values?.municipality?.province || ''}
                    disabled
                  />
                </Grid>
              </Fragment>
            )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <PmiTextField
                label="ID Nazionale dell'atto da certificare"
                aria-label="ID Nazionale dell'atto da certificare"
                name="idNational"
                id="idNational"
                fullWidth
                type="number"
                value={values?.idNational || ''}
                onChange={(event) => onChange('idNational', event.target.value)}
              />
            </Grid>
            <Grid container spacing={2} item xs={12} display="flex" justifyContent="flex-end">
              <Grid item xs={12} md={2}>
                <PmiTooltipError show={showTooltip} message={tooltipMessage}>
                  <span>
                    <PmiButton
                      variant="outlined"
                      color="cdmBrandThird"
                      fullWidth
                      onClick={handleSearch}
                    >
                      Cerca
                    </PmiButton>
                  </span>
                </PmiTooltipError>
              </Grid>
            </Grid>
            {dataPersons.length > 1 && visibilityTable ? (
              <Grid item xs={12}>
                <Table
                  ariaLabel="Lista Persone"
                  page={paginationState.page}
                  rowsPerPage={paginationState.rowsPerPage}
                  body={dataPersons}
                  count={dataPersons.length}
                  head={headCells}
                  onSort={noop}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  onVisibilityPress={noop}
                  orders={{}}
                  isNotLayoutFixed
                  noSort
                  hideButtons
                />
              </Grid>
            ) : undefined}
          </Fragment>
        ) : undefined}
      </Grid>
    </Fragment>
  );
};

AccordionAccountholderFormComponent.displayName = 'Accordion accountholder form component';

const AccordionAccountholderForm = memo(AccordionAccountholderFormComponent);

export default AccordionAccountholderForm;
